import dayjs from 'dayjs'
import { ScriptingFreq } from './templates'
import { uniqBy } from 'lodash'

export const handleFreqChange = (value) => {
  let tmpOptions = []

  if (value) {
    ScriptingFreq.forEach((option) => {
      let checkMatch = false
      let optionValue = ''
      if (option.label.toLocaleLowerCase().match(value.toLocaleLowerCase())) {
        checkMatch = true
        optionValue = option.value
      }
      if (checkMatch) {
        tmpOptions.push({
          value: optionValue,
          label: optionValue
        })
      }
    })
  } else {
    tmpOptions = ScriptingFreq
  }
  return tmpOptions
}

export const generateMedLabel = (med) => {
  let strParams = [med.name, med.strengthMetric1, med.unitOfMeasure1, med.presentationCode]
  if (med.scriptingDosage) {
    strParams.push(`- ${med.scriptingDosage}`)
  }
  let str = strParams.filter(Boolean).join(' ')
  return str
}

export const validateChronicIllnesses = (patientChronicDetails, script, action) => {
  let responseMessages = []
  let existingIllnesses = []
  let newIllnesses = []

  // EXISTING ILLNESSES NEW INSTANCE
  existingIllnesses = patientChronicDetails?.chronicIllnesses?.list
    ? JSON.parse(JSON.stringify(patientChronicDetails?.chronicIllnesses?.list))
    : []

  // UNIQUE ILLNESSES ON SCRIPT
  let illnessesOnScript = uniqBy(
    script.items
      .filter((i) => i.isChronic)
      .map((i) => {
        return { code: i.icd?.code, description: i.icd?.description, dateDiagnosed: dayjs() }
      }),
    'code'
  )

  // NEW ILLNESSES
  illnessesOnScript.forEach((illness) => {
    let existingIllnessMatchIndex = existingIllnesses.findIndex((i) => i.code === illness.code)
    // NOUT FOUND
    if (existingIllnessMatchIndex === -1) {
      let newIllness = illness
      let newIllnessMedications = []

      script.items.forEach((i) => {
        if (i?.icd?.code === illness.code) {
          newIllnessMedications.push({ med: i, startDate: dayjs(), endDate: undefined })
        }
      })
      newIllness.medications = newIllnessMedications
      newIllnesses.push(newIllness)
    }
  })

  // EXISTING ILLNESSES
  existingIllnesses.forEach((illness, index) => {
    let existingMedications = [...illness.medications]

    // ONLY CHRONIC MEDS FOR MATCHING ILLNESS
    let medicationsOnScript = script.items
      .filter((i) => i.isChronic && i.icd?.code === illness.code)
      .map((i) => ({ startDate: dayjs(), endDate: undefined, med: { ...i } }))

    // VALIDATE EXISTING MEDICATIONS
    medicationsOnScript.forEach((medication) => {
      const existingMedIndex = existingMedications.findIndex((i) => i?.med?.code === medication?.med?.code)
      if (existingMedIndex === -1) {
        // ADD NEW MEDICATION
        existingMedications.push({ ...medication })
      } else {
        // UPDATE EXISTING MEDICATION
        existingMedications[existingMedIndex] = {
          ...existingMedications[existingMedIndex],
          med: medication.med
        }
      }
    })
    // SET EXISTING ILLNESS MEDICATIONS
    existingIllnesses[index].medications = existingMedications
  })

  // CONFIRMATIO MESSAGES
  newIllnesses.forEach((illness) => {
    responseMessages.push(
      // COMPONENT
      <>
        <p>A new Chronic Illness has been captured</p>
        <p>
          <b>{illness.description?.toUpperCase() + ' ' + illness.code?.toUpperCase()}</b>
        </p>
        <p>With the following medications:</p>
        <ul style={{ marginTop: 0 }}>
          {illness.medications.map((med) => {
            return <li>{med.med.name}</li>
          })}
        </ul>
      </>
    )
  })

  // RESPONSE
  return action === 'confirmation' ? responseMessages : [...existingIllnesses, ...newIllnesses]
}

export const getNextDueDate = (date, scheduleInterval) => {
  let tmpNextDue = null
  // If date is undefined then that means that this is a new creation as there is no "nextDue" date.
  if (date) {
    // Here it is either due TODAY or it is PAST the due date either way we still add 30 days onto the previos "nextDue" date
    tmpNextDue = dayjs(date).add(scheduleInterval * 30, 'days')
  } else {
    // Here there was never a "nextDue" date as it is the first time the record is being created
    tmpNextDue = dayjs().add(30, 'days').format('YYYY-MM-DD')
  }
  return tmpNextDue
}

export const prepPatientQuery = (string) => {
  return {
    $or: [
      {
        $expr: {
          $regexMatch: {
            input: { $concat: ['$firstName', ' ', '$lastName'] },
            regex: string, //Your text search here
            options: 'i'
          }
        }
      },
      { phoneNumber: { $regex: string, $options: 'i' } },
      { email: { $regex: string, $options: 'i' } },
      { physicalAddress: { $regex: string, $options: 'i' } },
      { idNo: { $regex: string, $options: 'i' } }
    ]
  }
}

export const validateScript = (script, dischemApplicable) => {
  let errors = []
  // VALIDATE ICD IF CHRONIC
  script.items.forEach((scriptItem) => {
    if (scriptItem.isChronic || dischemApplicable) {
      if (!scriptItem.icd) {
        errors.push(
          dischemApplicable
            ? 'Dischem requires ICD10 codes for all medications'
            : 'Chronic medications require an ICD Diagnosis'
        )
      }

      if (dischemApplicable) {
        if (!scriptItem.dose || scriptItem.dose < 1) {
          errors.push('Dischem requires a dosage for all medications')
        }
      }
    }
  })

  return errors
}

/**
 * Formats medication names from UPPERCASE to a more readable format
 * @param {string} medicationName - The medication name in uppercase
 * @returns {string} - Formatted medication name
 */
export const formatMedicationName = (medicationName) => {
  if (!medicationName || typeof medicationName !== 'string') {
    return medicationName || ''
  }

  // Handle special abbreviations that should remain uppercase
  const uppercaseWords = [
    'NOV',
    'MG',
    'ML',
    'MCG',
    'KG',
    'IU',
    'HIV',
    'AIDS',
    'DNA',
    'RNA',
    'ECG',
    'EEG',
    'IV',
    'IM',
    'PO',
    'PR',
    'SC',
    'SL',
    'TD',
    'TOP',
    'VAG',
    'INH',
    'NASAL',
    'OPH',
    'OTIC',
    'DERM',
    'GI',
    'CV',
    'CNS',
    'GU',
    'RESP',
    'ENDO',
    'HEME',
    'ONCO',
    'PSYCH',
    'NEURO',
    'ORTHO',
    'URO',
    'GYN',
    'OB',
    'PEDS',
    'GERI',
    'EMERG',
    'ICU',
    'CCU',
    'ER',
    'OR',
    'PACU',
    'NICU',
    'PICU',
    'SICU',
    'MICU',
    'CVICU',
    'XR',
    'SR',
    'ER',
    'CR',
    'LA',
    'SA',
    'DR',
    'IR',
    'MR',
    'PR',
    'TR',
    'ODT',
    'SL',
    'BUCCAL',
    'NASAL',
    'OPH',
    'OTIC',
    'TOP',
    'VAG',
    'RECT',
    'TD',
    'PATCH',
    'GEL',
    'CREAM',
    'OINT',
    'LOT',
    'SOLN',
    'SUSP',
    'EMU',
    'FOAM',
    'SPRAY',
    'DROPS',
    'CAPS',
    'TABS',
    'CHEW',
    'GRAN',
    'POWD',
    'LIQD',
    'SYRUP',
    'ELIX',
    'TINCT',
    'EXTR',
    'INJ',
    'VIAL',
    'AMP',
    'PRE',
    'SYR',
    'KIT',
    'PACK',
    'DOSE',
    'QD',
    'BID',
    'TID',
    'QID',
    'QOD',
    'HS',
    'AC',
    'PC',
    'PRN',
    'STAT',
    'ASAP',
    'AIDS',
    'HIV',
    'HCV',
    'HBV',
    'HPV',
    'HSV',
    'CMV',
    'EBV',
    'VZV',
    'RSV',
    'NSAID',
    'ACE',
    'ARB',
    'CCB',
    'SSRI',
    'SNRI',
    'TCA',
    'MAOI',
    'ADHD',
    'COPD',
    'GERD',
    'IBD',
    'IBS',
    'UTI',
    'STI',
    'STD',
    'DVT',
    'PE',
    'MI',
    'CHF',
    'HTN',
    'DM',
    'T1DM',
    'T2DM',
    'CKD',
    'ESRD',
    'CAD',
    'PVD',
    'CVA',
    'TIA',
    'AF',
    'VT',
    'VF',
    'SVT',
    'PAD',
    'AAA',
    'CABG',
    'PCI',
    'PTCA',
    'AND',
    'OR',
    'WITH',
    'WITHOUT',
    'FOR',
    'AS',
    'IN',
    'ON',
    'OF',
    'TO',
    'FROM'
  ]

  // Handle chemical/dosage units that should remain uppercase
  const dosageUnits = ['MG', 'ML', 'MCG', 'IU', 'G', 'KG', 'L', 'DL', 'CL', 'MM', 'CM', 'M']

  // Handle percentage symbols and numbers
  const percentagePattern = /(\d+\.?\d*)\s*%/g
  const numberPattern = /\b(\d+\.?\d*)\s*(MG|ML|MCG|G|IU|KG|L|DL|CL|MM|CM|M)\b/gi

  return (
    medicationName
      .toLowerCase()
      .split(/\s+/)
      .map((word) => {
        // Keep numbers with units as-is after converting to uppercase
        if (/^\d+\.?\d*(mg|ml|mcg|g|iu|kg|l|dl|cl|mm|cm|m)$/i.test(word)) {
          return word.toUpperCase()
        }

        // Keep pure numbers as-is
        if (/^\d+\.?\d*$/.test(word)) {
          return word
        }

        // Keep percentages formatted properly
        if (/^\d+\.?\d*%$/.test(word)) {
          return word
        }

        // Keep specific abbreviations in uppercase
        if (uppercaseWords.includes(word.toUpperCase())) {
          return word.toUpperCase()
        }

        // Handle parentheses content specially
        if (word.startsWith('(') && word.endsWith(')')) {
          const innerWord = word.slice(1, -1)
          if (uppercaseWords.includes(innerWord.toUpperCase())) {
            return `(${innerWord.toUpperCase()})`
          }
          return `(${innerWord.charAt(0).toUpperCase() + innerWord.slice(1).toLowerCase()})`
        }

        // Handle words with parentheses
        if (word.startsWith('(')) {
          const innerWord = word.slice(1)
          if (uppercaseWords.includes(innerWord.toUpperCase())) {
            return `(${innerWord.toUpperCase()}`
          }
          return `(${innerWord.charAt(0).toUpperCase() + innerWord.slice(1).toLowerCase()}`
        }

        if (word.endsWith(')')) {
          const innerWord = word.slice(0, -1)
          if (uppercaseWords.includes(innerWord.toUpperCase())) {
            return `${innerWord.toUpperCase()})`
          }
          return `${innerWord.charAt(0).toUpperCase() + innerWord.slice(1).toLowerCase()})`
        }

        // Handle hyphenated words
        if (word.includes('-')) {
          return word
            .split('-')
            .map((part) => {
              if (uppercaseWords.includes(part.toUpperCase())) {
                return part.toUpperCase()
              }
              return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
            })
            .join('-')
        }

        // Default: Title case
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join(' ')
      // Fix spacing around percentages and dosages
      .replace(/(\d+)\s*%/g, '$1%')
      .replace(/(\d+\.?\d*)\s+(mg|ml|mcg|g|iu|kg|l|dl|cl|mm|cm|m)\b/gi, '$1 $2'.toUpperCase())
  )
}
